import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { FC, useCallback, useMemo, useState } from 'react';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { EMarketAction } from '@/lib/types/market-action';
import { EMarketIntent } from '@/lib/types/market-intent';
import { EMarketOrderType } from '@/lib/types/market-order-type';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';
import { Badge } from '../ui-kit/badge';
import { Icon } from '../ui-kit/icons';
import { Text } from '../ui-kit/text';
import { cn } from '../ui-kit/utils';
import { Button as ButtonV2 } from '../ui-kit-v2/button';
import { DialogTakeProfitStopLoss } from './DialogTakeProfitStopLoss';
import { DialogSharePosition } from './SharePosition';

interface EventOpenPositionsCardProps {
  position: {
    id: string;
    outcome: 'Yes' | 'No';
    shares: number;
    openPrice: string;
    value: string;
    cost: string;
    /** Raw figures for the share dialog — never parse them back out of the strings above. */
    costValue: number;
    openPriceCents: number;
    pnl: {
      amount: string;
      percent: string;
    };
    tpSl?: {
      ordersCount: number;
      takeProfitPrice?: number;
      stopLossPrice?: number;
      totalTpSize?: number;
      totalSlSize?: number;
    } | null;
    eventTokenId: string;
    totalShares?: number;
  };
  eventId: string;
  outcomeId: string;
  quoteAssetId: string;
  avgOpenPrice: number;
  currentPrice: number;
  eventTitle?: string;
  eventImage?: string;
}

export const EventOpenPositionsCard: FC<EventOpenPositionsCardProps> = ({
  position,
  eventId,
  outcomeId,
  quoteAssetId,
  avgOpenPrice,
  currentPrice,
  eventTitle,
  eventImage,
}) => {
  const [openTpSlDialog, setOpenTpSlDialog] = useState(false);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const selectedOutcome = useSelectedOutcome();
  const t = useTranslations('event.openPositions');

  const setAction = useOrderFormStore.use.setAction();
  const setIntent = useOrderFormStore.use.setIntent();
  const setOrderType = useOrderFormStore.use.setOrderType();
  const setSharesCount = useOrderFormStore.use.setSharesCount();
  const setLimitPrice = useOrderFormStore.use.setLimitPrice();
  const setAmountUsd = useOrderFormStore.use.setAmountUsd();
  const setIsMobileFormOpened = useOrderFormStore.use.setIsMobileFormOpened();
  const triggerLightningAnimation = useOrderFormStore.use.triggerLightningAnimation();

  const handleTpSlClick = useCallback(() => {
    setOpenTpSlDialog(true);
  }, []);

  const handleShareClick = useCallback(() => {
    setOpenShareDialog(true);
  }, []);

  // Calculate share position data
  const sharePositionData = useMemo(() => {
    const myBet = position.costValue;
    const avgPriceCents = position.openPriceCents;

    // Possible win = shares × $1 (assuming each share pays $1 if correct)
    const possibleWin = position.shares;

    // Position wins when the resolution for the held side (Yes/No) is the winning one
    const isResolved = selectedOutcome?.status === 'RESOLVED';
    const sideResolution = selectedOutcome?.r?.find(
      (res) => res.isPositive === (position.outcome === 'Yes'),
    );

    const isWon = isResolved && sideResolution?.isWin === true;

    return {
      eventTitle: eventTitle || '',
      eventImage: eventImage || '/evfallback.webp',
      outcome: position.outcome,
      outcomeName: selectedOutcome?.groupItemTitle,
      isResolved,
      isWon,
      entryPrice: avgPriceCents,
      // Resolved token settles at 100¢ (won) or 0¢ (lost); still open → no exit yet
      exitPrice: isResolved ? (isWon ? 100 : 0) : undefined,
      myBet,
      possibleWin,
    };
  }, [position, eventTitle, eventImage, selectedOutcome]);

  const handleSellClick = useCallback(() => {
    // Set action to SELL
    setAction(EMarketAction.SELL);

    // Set intent based on outcome (Yes -> YES, No -> NO)
    setIntent(position.outcome === 'Yes' ? EMarketIntent.YES : EMarketIntent.NO);

    // Set order type to MARKET
    setOrderType(EMarketOrderType.MARKET);

    // Set shares count from position
    setSharesCount(new Decimal(position.shares));

    // Clear limitPrice and amountUsd (not needed for market orders)
    setLimitPrice(new Decimal(0));
    setAmountUsd(new Decimal(0));

    // Open mobile form
    setIsMobileFormOpened(true);

    // Trigger lightning animation on OrderForm
    triggerLightningAnimation();
  }, [
    position.outcome,
    position.shares,
    setAction,
    setIntent,
    setOrderType,
    setSharesCount,
    setLimitPrice,
    setAmountUsd,
    setIsMobileFormOpened,
    triggerLightningAnimation,
  ]);

  return (
    <>
      <div className="bg-v2-surface-secondary rounded-[10px] p-4">
        {/* Desktop layout (>768px) */}
        <div className="hidden grid-cols-[180px_120px_120px_180px_1fr] items-center gap-6 md:grid">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <Badge
                className={cn(
                  'rounded-[8px] px-2 py-[2px] text-[12px] leading-4 tracking-[-0.12px]',
                  position.outcome === 'No'
                    ? 'bg-v2-red-surface text-red-400'
                    : 'bg-v2-green-surface text-v2-green',
                )}
              >
                {position.outcome} {position.openPrice}
              </Badge>
              <Text variant="body-xs" className="text-v2-text-secondary">
                {formatMoney(position.shares, { decimals: 2 })} {t('shares')}
              </Text>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('cost')}
            </Text>
            <Text variant="body-small" className="whitespace-nowrap">
              {position.cost}
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('value')}
            </Text>
            <Text variant="body-small" className="whitespace-nowrap">
              {position.value}
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('tpSl')}
            </Text>
            {position.tpSl ? (
              <button type="button" onClick={handleTpSlClick} className="cursor-pointer text-left">
                <Text variant="body-small" className="text-v2-text-secondary">
                  {position.tpSl.ordersCount === 1 ? (
                    <>
                      TP:{' '}
                      <span className="text-v2-text-primary underline decoration-dotted">
                        {position.tpSl.takeProfitPrice
                          ? formatCents(position.tpSl.takeProfitPrice * 100)
                          : '-'}
                      </span>{' '}
                      /SL:{' '}
                      <span className="text-v2-text-primary underline decoration-dotted">
                        {position.tpSl.stopLossPrice
                          ? formatCents(position.tpSl.stopLossPrice * 100)
                          : '-'}
                      </span>
                    </>
                  ) : (
                    <>
                      TP:{' '}
                      <span className="text-v2-text-primary underline decoration-dotted">
                        {position.tpSl.totalTpSize
                          ? formatMoney(position.tpSl.totalTpSize, { decimals: 2 })
                          : '-'}
                      </span>{' '}
                      /SL:{' '}
                      <span className="text-v2-text-primary underline decoration-dotted">
                        {position.tpSl.totalSlSize
                          ? formatMoney(position.tpSl.totalSlSize, { decimals: 2 })
                          : '-'}
                      </span>
                    </>
                  )}
                </Text>
              </button>
            ) : (
              <ButtonV2 appearance="outline" size="S" onClick={handleTpSlClick}>
                {t('add')}
              </ButtonV2>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ButtonV2
              appearance="neutral"
              size="M"
              onClick={handleSellClick}
              className="!bg-v2-red-surface !text-red-400 hover:!bg-v2-red-surface/80"
            >
              {t('sell')}
            </ButtonV2>
            <ButtonV2 appearance="icon" size="M" onClick={handleShareClick} aria-label="Share">
              <Icon name="Share2" size={20} />
            </ButtonV2>
          </div>
        </div>

        {/* Tablet layout (560-768px) */}
        <div
          className="hidden grid-cols-[minmax(100px,140px)_minmax(70px,90px)_minmax(70px,90px)_auto_auto] items-center
            gap-3 min-[560px]:grid md:hidden"
        >
          <div className="flex flex-col gap-2">
            <Badge
              className={cn(
                'rounded-[8px] px-2 py-[2px] text-[12px] leading-4 tracking-[-0.12px]',
                position.outcome === 'No'
                  ? 'bg-v2-red-surface text-red-400'
                  : 'bg-v2-green-surface text-v2-green',
              )}
            >
              {position.outcome} {position.openPrice}
            </Badge>
            <Text variant="body-xs" className="text-v2-text-secondary">
              {formatMoney(position.shares, { decimals: 2 })} {t('shares')}
            </Text>
          </div>

          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('cost')}
            </Text>
            <Text variant="body-small" className="whitespace-nowrap">
              {position.cost}
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('value')}
            </Text>
            <Text variant="body-small" className="whitespace-nowrap">
              {position.value}
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('tpSl')}
            </Text>
            {position.tpSl ? (
              <button type="button" onClick={handleTpSlClick} className="cursor-pointer text-left">
                <Text variant="body-small" className="text-v2-text-secondary">
                  {position.tpSl.ordersCount === 1 ? (
                    <>
                      TP:{' '}
                      <span className="text-v2-text-primary underline decoration-dotted">
                        {position.tpSl.takeProfitPrice
                          ? formatCents(position.tpSl.takeProfitPrice * 100)
                          : '-'}
                      </span>{' '}
                      /SL:{' '}
                      <span className="text-v2-text-primary underline decoration-dotted">
                        {position.tpSl.stopLossPrice
                          ? formatCents(position.tpSl.stopLossPrice * 100)
                          : '-'}
                      </span>
                    </>
                  ) : (
                    <>
                      TP:{' '}
                      <span className="text-v2-text-primary underline decoration-dotted">
                        {position.tpSl.totalTpSize
                          ? formatMoney(position.tpSl.totalTpSize, { decimals: 2 })
                          : '-'}
                      </span>{' '}
                      /SL:{' '}
                      <span className="text-v2-text-primary underline decoration-dotted">
                        {position.tpSl.totalSlSize
                          ? formatMoney(position.tpSl.totalSlSize, { decimals: 2 })
                          : '-'}
                      </span>
                    </>
                  )}
                </Text>
              </button>
            ) : (
              <ButtonV2 appearance="outline" size="S" onClick={handleTpSlClick}>
                {t('add')}
              </ButtonV2>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <ButtonV2
              appearance="neutral"
              size="S"
              onClick={handleSellClick}
              className="!bg-v2-red-surface !text-red-400 hover:!bg-v2-red-surface/80"
            >
              {t('sell')}
            </ButtonV2>
            <ButtonV2 appearance="icon" size="M" onClick={handleShareClick} aria-label="Share">
              <Icon name="Share2" size={20} />
            </ButtonV2>
          </div>
        </div>

        {/* Mobile layout (≤500px) */}
        <div className="flex flex-col gap-4 min-[560px]:hidden">
          {/* Header with Badge and shares */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge
                className={cn(
                  'rounded-[8px] px-2 py-[2px] text-[12px] leading-4 tracking-[-0.12px]',
                  position.outcome === 'No'
                    ? 'bg-v2-red-surface text-red-400'
                    : 'bg-v2-green-surface text-v2-green',
                )}
              >
                {position.outcome} {position.openPrice}
              </Badge>
              <Text variant="body-small" className="text-v2-text-secondary">
                {formatMoney(position.shares, { decimals: 2 })} {t('shares')}
              </Text>
            </div>
            <ButtonV2 appearance="icon" size="S" onClick={handleShareClick} aria-label="Share">
              <Icon name="Share2" size={20} />
            </ButtonV2>
          </div>

          {/* Grid fields */}
          <div className="grid grid-cols-[1fr_1fr] gap-4">
            {/* Cost */}
            <div className="flex flex-col gap-1">
              <Text variant="body-small" className="text-v2-text-secondary">
                {t('cost')}
              </Text>
              <Text variant="body-small" className="text-v2-text-primary">
                {position.cost}
              </Text>
            </div>

            {/* Value */}
            <div className="flex flex-col gap-1">
              <Text variant="body-small" className="text-v2-text-secondary">
                {t('value')}
              </Text>
              <Text variant="body-small" className="text-v2-text-primary">
                {position.value}
              </Text>
            </div>

            {/* TP/SL */}
            <div className="flex flex-col gap-1">
              <Text variant="body-small" className="text-v2-text-secondary">
                {t('tpSl')}
              </Text>
              {position.tpSl ? (
                <button
                  type="button"
                  onClick={handleTpSlClick}
                  className="cursor-pointer text-left"
                >
                  <Text variant="body-small" className="text-v2-text-secondary">
                    {position.tpSl.ordersCount === 1 ? (
                      <>
                        TP:{' '}
                        <span className="text-v2-text-primary underline decoration-dotted">
                          {position.tpSl.takeProfitPrice
                            ? formatCents(position.tpSl.takeProfitPrice * 100)
                            : '-'}
                        </span>{' '}
                        /SL:{' '}
                        <span className="text-v2-text-primary underline decoration-dotted">
                          {position.tpSl.stopLossPrice
                            ? formatCents(position.tpSl.stopLossPrice * 100)
                            : '-'}
                        </span>
                      </>
                    ) : (
                      <>
                        TP:{' '}
                        <span className="text-v2-text-primary underline decoration-dotted">
                          {position.tpSl.totalTpSize ? Math.floor(position.tpSl.totalTpSize) : '-'}
                        </span>{' '}
                        /SL:{' '}
                        <span className="text-v2-text-primary underline decoration-dotted">
                          {position.tpSl.totalSlSize ? Math.floor(position.tpSl.totalSlSize) : '-'}
                        </span>
                      </>
                    )}
                  </Text>
                </button>
              ) : (
                <ButtonV2 appearance="outline" size="S" onClick={handleTpSlClick}>
                  {t('add')}
                </ButtonV2>
              )}
            </div>

            {/* PnL */}
            <div className="flex flex-col gap-1">
              <Text variant="body-small" className="text-v2-text-secondary">
                {t('pnl')}
              </Text>
              <Text variant="body-small" className="text-v2-text-primary">
                {position.pnl.amount} ({position.pnl.percent})
              </Text>
            </div>
          </div>

          {/* Sell button */}
          <ButtonV2
            appearance="neutral"
            size="M"
            onClick={handleSellClick}
            className="w-full !bg-v2-red-surface !text-red-400 hover:!bg-v2-red-surface/80"
          >
            {t('sell')}
          </ButtonV2>
        </div>
      </div>
      {openTpSlDialog && (
        <DialogTakeProfitStopLoss
          source="event_page"
          open={openTpSlDialog}
          onOpenChange={setOpenTpSlDialog}
          assetId={position.eventTokenId}
          totalShares={position.totalShares ?? position.shares}
          _availableShares={position.shares}
          eventId={eventId}
          eventOutcomeId={outcomeId}
          baseAssetId={position.eventTokenId}
          quoteAssetId={quoteAssetId}
          intent={position.outcome === 'Yes' ? 'YES' : 'NO'}
          side="SELL"
          avgOpenPrice={avgOpenPrice}
          currentPrice={currentPrice}
        />
      )}
      {openShareDialog && (
        <DialogSharePosition
          open={openShareDialog}
          onOpenChange={setOpenShareDialog}
          position={sharePositionData}
        />
      )}
    </>
  );
};
